import React from 'react';
import { IntlProvider } from 'react-intl';

import { RecommendStandardOverPremiumModal } from '@atlassian/recommend-standard-over-premium';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';

import { usePricingEOTPremiumToStandardQuery } from '../../hooks/use-pricing-end-of-trial/usePricingEndOfTrial.ts';
import { useEnrolledAIInEditorModalsExperiment } from '../../hooks/use-is-eligible-for-ai-in-editor/useEnrolledAIInEditorModalsExperiment.ts';
import { useAnalytics, ScreenEvent } from '../../utils/analytics.ts';
import { recordModalShown } from '../../utils/modalDisplayUtils.ts';
import { useModalState } from '../../utils/useModalState.ts';

export const RecommendStandardModal = ({
	trialEndDate,
	trialDaysLeft,
}: {
	trialEndDate: string;
	trialDaysLeft: number;
}) => {
	const { dismissedEndOfTrialModal, clickedEndOfTrialPrimaryCTA } = useAnalytics();
	const { useChoreographedRender } = useChoreographerAssets();
	const shouldRenderMyComponent = useChoreographedRender({
		messageId: 'experiment-ai-in-editor-end-of-trial-modal-container',
	});
	const {
		data: endOfTrialData,
		loading,
		error,
	} = usePricingEOTPremiumToStandardQuery(!shouldRenderMyComponent);

	const { isOpen, handleClose } = useModalState();

	const isEligible = Boolean(
		endOfTrialData &&
		shouldRenderMyComponent &&
		!loading &&
		!error &&
		endOfTrialData.userCanAddPaymentDetails &&
		endOfTrialData.experienceUrl &&
		endOfTrialData.premiumPrice &&
		endOfTrialData.standardPrice &&
		isOpen,
	);

	const { isABTreatment } = useEnrolledAIInEditorModalsExperiment({
		isEligible,
		touchpoint: 'end-of-trial',
		trialDaysLeft,
	});

	if (!endOfTrialData) {
		return null;
	}

	const { experienceUrl, premiumPrice, standardPrice } = endOfTrialData;

	if (
		!isEligible ||
		!isABTreatment ||
		!endOfTrialData ||
		loading ||
		error ||
		!experienceUrl ||
		!premiumPrice ||
		!standardPrice
	) {
		return null;
	}

	const handleDismiss = () => {
		handleClose();
		recordModalShown();
		dismissedEndOfTrialModal();
	};

	const handlePrimaryCTAClick = () => {
		handleClose();
		window.open(experienceUrl, '_blank');
		recordModalShown();
		clickedEndOfTrialPrimaryCTA();
	};

	return (
		<IntlProvider locale="en" messages={{}}>
			<>
				<RecommendStandardOverPremiumModal
					analyticsAttributes={{}}
					onClickBuyStandard={handlePrimaryCTAClick}
					onClose={handleDismiss}
					premiumPrice={premiumPrice}
					productKey="Confluence"
					standardPrice={standardPrice}
					trialEndDate={new Date(trialEndDate)}
				/>
				<ScreenEvent name="endOfTrialModal" />
			</>
		</IntlProvider>
	);
};
